// Helpers y constantes compartidos entre las pestañas del perfil de
// paciente (InfoTab, AppointmentsTab, RecetasTab, ComunicacionesTab,
// PatientSidebar) — extraídos junto con el resto del split de
// PatientProfile.js (1349 líneas en un solo archivo).

export const tryParseJson = (str, fallback = []) => {
  try { return JSON.parse(str); } catch { return fallback; }
};

export const STATUS_RX = {
  borrador: { label: 'Borrador', color: '#9e9e9e' },
  emitida:  { label: 'Emitida',  color: '#2e7d32' },
  vencida:  { label: 'Vencida',  color: '#c62828' },
};

export const GENDER_OPTIONS = ['masculino', 'femenino', 'otro'];
export const BLOOD_TYPES    = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
export const emptyRxItem    = () => ({ drug_name: '', concentration: '', pharmaceutical_form: '', quantity: '', instructions: '', is_generic: true, _drugOptions: [], _selectedDrug: null });

export const MSG_TYPE_LABELS = {
  receta: 'Receta',
  indicaciones: 'Indicaciones',
  recordatorio: 'Recordatorio',
  otro: 'Mensaje',
};

// El backend guarda created_at/sent_at como naive-UTC (sin offset) — sin
// agregar la 'Z' el navegador los interpreta como hora LOCAL, mostrando
// todo ~3hs adelantado en Argentina. Mismo criterio que el resto de la
// app usa para start_time de turnos.
const _asUtcDate = (d) => new Date(typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d);

export const fmtDate = (d) => d ? _asUtcDate(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
export const fmtDateTime = (d) => d ? _asUtcDate(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

// Abre WhatsApp con mensaje opcional. Normaliza el número (solo dígitos).
export const openWhatsApp = (phone, text = '') => {
  const digits = phone.replace(/\D/g, '');
  const url = `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

// ── Imprimir HC ───────────────────────────────────────────────────────────────
// clinic: objeto Clinic (nombre/dirección/teléfono) — antes esta función no
// recibía nada de la clínica, ni siquiera mostraba su nombre. logoDataUrl:
// data: URI ya resuelta por el caller (el logo vive atrás de un endpoint
// autenticado, un <img src> directo en la ventana nueva de impresión no
// manda el header de Authorization — por eso se resuelve afuera, como blob
// autenticado, y se pasa ya convertida a data URI).
export const printHC = (patient, records, clinic = null, logoDataUrl = null) => {
  // fmt() se usa tanto para patient.dob (fecha pura, sin hora relevante) como
  // para new Date() ya construido — no necesita el ajuste de 'Z'. fmtLong/
  // fmtTime solo se usan para record.created_at (timestamp real, guardado
  // naive-UTC en el backend), así que sí necesitan tratarlo como UTC.
  const fmt = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const asUtc = (d) => typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d;
  const fmtLong = (d) => d ? new Date(asUtc(d)).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const fmtTime = (d) => d ? new Date(asUtc(d)).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '';
  const esc = (s) => String(s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const section = (label, value) => value
    ? `<div class="section"><span class="label">${label}</span><p>${esc(value).replace(/\n/g, '<br>')}</p></div>` : '';

  const age = patient.dob
    ? Math.floor((new Date() - new Date(patient.dob)) / (365.25 * 24 * 3600 * 1000))
    : null;

  const patientInfo = [
    ['Nombre', patient.name],
    ['DNI', patient.dni],
    ['Edad', age != null ? `${age} años${patient.dob ? ` (nac. ${fmt(patient.dob)})` : ''}` : (patient.dob ? fmt(patient.dob) : null)],
    ['Sexo', patient.gender],
    ['Grupo sanguíneo', patient.blood_type],
    ['Teléfono', patient.phone],
    ['Obra social', patient.insurance_provider ? `${patient.insurance_provider}${patient.insurance_policy_number ? ` — ${patient.insurance_policy_number}` : ''}` : null],
    ['Alergias', patient.allergies],
    ['Medicación actual', patient.current_medications],
    ['Antecedentes', patient.chronic_diseases],
  ].filter(([, v]) => v);

  const patientInfoHtml = patientInfo.map(([label, value]) =>
    `<div class="pinfo-row"><span class="pinfo-label">${label}:</span> ${esc(value)}</div>`
  ).join('');

  const VITAL_LABELS = {
    vital_weight_kg: 'Peso', vital_height_cm: 'Talla', vital_blood_pressure: 'TA',
    vital_heart_rate: 'FC', vital_temperature_c: 'Temp', vital_oxygen_sat: 'SatO₂',
  };

  const recordsHtml = records.map(r => {
    const metaParts = [];
    if (r.doctor_name) metaParts.push(`Dr/a. ${r.doctor_name}`);
    if (r.created_by_username && r.created_by_username !== r.doctor_name) metaParts.push(`cargado por ${r.created_by_username}`);

    // Campo único de texto libre (post-simplificación de la HC). Fallback a
    // los campos legado para consultas viejas que no llegaron a migrarse.
    const body = r.notes
      ? section('Evolución', r.notes)
      : (section('Motivo de consulta', r.chief_complaint) +
         section('Examen / Hallazgos', r.examination_findings) +
         section('Diagnóstico', r.diagnosis) +
         section('Tratamiento', r.treatment)) || '<p style="color:#888;margin:0">Sin evolución cargada.</p>';

    const vitals = Object.entries(VITAL_LABELS)
      .filter(([k]) => r[k]).map(([k, label]) => `${label}: ${esc(r[k])}`).join(' · ');

    return `
    <div class="record">
      <div class="record-header">
        <div>
          <span class="date">${fmtLong(r.created_at)}${r.created_at ? ` — ${fmtTime(r.created_at)} hs` : ''}</span>
          ${metaParts.length ? `<div class="record-meta">${esc(metaParts.join(' · '))}</div>` : ''}
        </div>
        ${r.consultation_type ? `<span class="chip">${esc(r.consultation_type)}</span>` : ''}
      </div>
      ${body}
      ${vitals ? `<div class="section"><span class="label">Signos vitales</span><p>${vitals}</p></div>` : ''}
    </div>
  `;
  }).join('');

  const clinicLine = clinic
    ? [clinic.address, clinic.phone ? `Tel.: ${clinic.phone}` : null].filter(Boolean).join(' · ')
    : '';
  const clinicHeaderHtml = clinic ? `
    <div class="clinic-header">
      ${logoDataUrl ? `<img class="clinic-logo" src="${logoDataUrl}" alt="" />` : ''}
      <div>
        <div class="clinic-name">${esc(clinic.name || '')}</div>
        ${clinicLine ? `<div class="clinic-line">${esc(clinicLine)}</div>` : ''}
      </div>
    </div>
  ` : '';

  const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
  <title>HC — ${patient.name}</title>
  <style>
    body{font-family:Arial,sans-serif;font-size:12px;color:#222;margin:0;padding:24px}
    h1{font-size:18px;margin:0 0 4px}.meta{color:#555;font-size:11px;margin-bottom:12px}
    .clinic-header{display:flex;align-items:center;gap:10px;margin-bottom:14px}
    .clinic-logo{width:44px;height:44px;object-fit:contain}
    .clinic-name{font-size:14px;font-weight:bold;color:#1565c0;text-transform:uppercase}
    .clinic-line{font-size:10.5px;color:#666}
    .pinfo{border:1px solid #ddd;border-radius:4px;padding:10px 14px;margin-bottom:16px;background:#fafafa}
    .pinfo-row{font-size:11.5px;line-height:1.6;display:inline-block;width:49%;vertical-align:top}
    .pinfo-label{font-weight:bold;color:#555}
    .record{border:1px solid #ddd;border-radius:4px;padding:12px;margin-bottom:14px;break-inside:avoid}
    .record-header{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:6px}
    .date{font-weight:bold;font-size:12px}.record-meta{color:#888;font-size:10px;margin-top:2px}
    .chip{background:#e3f2fd;color:#1565c0;padding:1px 7px;border-radius:12px;font-size:10px;white-space:nowrap}
    .section{margin-bottom:8px}.label{font-size:10px;font-weight:bold;text-transform:uppercase;color:#888;letter-spacing:.05em;display:block;margin-bottom:2px}
    .section p{margin:0;line-height:1.5}@media print{body{padding:0}}
  </style></head><body>
  ${clinicHeaderHtml}
  <h1>Historia Clínica — ${esc(patient.name)}</h1>
  <div class="meta">Generado el ${fmt(new Date())} · ${records.length} consulta${records.length !== 1 ? 's' : ''}</div>
  <div class="pinfo">${patientInfoHtml}</div>
  ${recordsHtml || '<p style="color:#888">Sin registros clínicos.</p>'}
  </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => w.print(), 400);
};
